import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  contentTagInputSchema,
  type ContentTag,
  type ContentTagInput,
} from "@/lib/data/schemas/automations";
import { saveContentTag } from "@/lib/data/requests/automations";
import { contentTagsQueryKey } from "@/lib/data/react-query/automations";
import { automationError } from "./automationError";

export function ContentTagEditor({
  tag,
  onClose,
}: {
  tag?: ContentTag;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<ContentTagInput>(
    () =>
      tag ?? {
        name: "",
        description: "",
        applicationGuidance: "",
        color: "#7c6ee6",
      },
  );
  const [validation, setValidation] = useState<string>();
  const client = useQueryClient();
  const save = useMutation({
    mutationFn: (input: ContentTagInput) => saveContentTag(input, tag?.id),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: contentTagsQueryKey });
      onClose();
    },
  });
  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        const result = contentTagInputSchema.safeParse({
          ...draft,
          applicationGuidance: draft.applicationGuidance?.trim() || null,
        });
        if (!result.success) {
          setValidation(result.error.issues[0].message);
          return;
        }
        setValidation(undefined);
        save.mutate(result.data);
      }}
    >
      <label className="block space-y-2 font-medium">
        Name
        <Input
          autoFocus
          value={draft.name}
          onChange={(event) => {
            setDraft({ ...draft, name: event.target.value });
          }}
          placeholder="Medical bill"
          maxLength={200}
        />
      </label>
      <label className="block space-y-2 font-medium">
        Description
        <textarea
          className="bg-background min-h-24 w-full rounded-md border p-3 text-sm font-normal"
          value={draft.description}
          onChange={(event) => {
            setDraft({ ...draft, description: event.target.value });
          }}
          placeholder="Bills and statements showing charges for medical treatment."
        />
      </label>
      <label className="block space-y-2 font-medium">
        When to apply{" "}
        <span className="text-muted-foreground font-normal">(optional)</span>
        <textarea
          className="bg-background min-h-28 w-full rounded-md border p-3 text-sm font-normal"
          value={draft.applicationGuidance ?? ""}
          onChange={(event) => {
            setDraft({ ...draft, applicationGuidance: event.target.value });
          }}
          placeholder="Apply to itemized medical bills and provider balance statements. Exclude treatment notes and explanations of benefits."
        />
      </label>
      <p className="text-muted-foreground text-sm">
        AI uses your description and guidance when processing new files.
        Existing files keep their tags until reprocessed.
      </p>
      <label className="flex items-center gap-3 font-medium">
        Color
        <input
          type="color"
          value={draft.color}
          onChange={(event) => {
            setDraft({ ...draft, color: event.target.value });
          }}
          className="bg-background h-9 w-12 cursor-pointer rounded border"
        />
      </label>
      {(validation || save.error) && (
        <p role="alert" className="text-destructive text-sm">
          {validation ?? automationError(save.error)}
        </p>
      )}
      <div className="flex justify-end gap-2 border-t pt-4">
        <Button
          type="button"
          variant="outline"
          disabled={save.isPending}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save tag"}
        </Button>
      </div>
    </form>
  );
}
